import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, BookOpen, Calendar, User, Search } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useAuthStore } from '@/stores/authStore';
import { useSchedulingTerminology } from '@/hooks/useSchedulingTerminology';

interface QuickAction {
  Icon: LucideIcon;
  label: string;
  to: string;
}

export const QuickActions: React.FC = () => {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const { tenant } = useAuthStore();
  const mode = tenant?.settings?.schedulingUiMode || 'both';
  const { createClassLabel, classesPageLink } = useSchedulingTerminology(mode);

  const actions: QuickAction[] = [
    { Icon: Plus, label: 'Add Student', to: '/admin/students' },
    { Icon: User, label: 'Add Staff', to: '/admin/staff' },
    { Icon: BookOpen, label: createClassLabel, to: classesPageLink },
    { Icon: Calendar, label: 'Manage Calendar', to: '/admin/calendar' },
    { Icon: Search, label: 'View Audit Logs', to: '/admin/audit-logs' },
  ];

  const containerStyle: React.CSSProperties = {
    marginBottom: '1rem',
  };

  const titleStyle: React.CSSProperties = {
    fontSize: '0.875rem',
    fontWeight: '600',
    marginBottom: '0.5rem',
    color: 'var(--text-primary)',
  };

  const actionsContainerStyle: React.CSSProperties = {
    display: 'flex',
    gap: '0.5rem',
    flexWrap: 'wrap',
  };

  const buttonBaseStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '0.375rem',
    padding: '0.5rem 0.875rem',
    backgroundColor: 'var(--primary-color)',
    color: 'var(--on-primary-color)',
    textDecoration: 'none',
    borderRadius: 'var(--radius)',
    fontSize: '0.75rem',
    fontWeight: '500',
    minHeight: '36px',
    transition: 'all 0.2s ease-in-out',
    boxShadow: 'var(--shadow)',
  };

  const buttonHoverStyle: React.CSSProperties = {
    transform: 'translateY(-1px)',
    boxShadow: 'var(--shadow-lg)',
    filter: 'brightness(1.1)',
  };

  const getButtonStyle = (index: number): React.CSSProperties => ({
    ...buttonBaseStyle,
    ...(hoveredIndex === index ? buttonHoverStyle : {}),
  });

  return (
    <div style={containerStyle}>
      <h2 style={titleStyle}>Quick Actions</h2>
      <div style={actionsContainerStyle}>
        {actions.map((action, index) => (
          <Link
            key={action.to}
            to={action.to}
            style={getButtonStyle(index)}
            onMouseEnter={() => setHoveredIndex(index)}
            onMouseLeave={() => setHoveredIndex(null)}
          >
            <action.Icon size={14} aria-hidden="true" />
            <span>{action.label}</span>
          </Link>
        ))}
      </div>
    </div>
  );
};
